import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { CloseOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";
import useHideOnScroll from "util/hooks/useHideOnScroll";
import {
  trackBannerClick,
  resolveBannerTargetUrl,
  openBannerTarget,
  PANEL_TYPE_B2B,
} from "util/BannerClickActions";
import FinekraOzelBadge from "assets/images/finekra-ozel-badge.png";
import "./BannerSlider.css";

const BannerSlider = ({
  screenName,
  panelType = PANEL_TYPE_B2B,
  hideOnScroll = true,
  controlParameter = true,
  isLogin = true,
  firmId = null,
  showBanner = true,
  tenantId = null,
  closable = false,
  onClose,
  isLoginPage = false,
}) => {
  const [banners, setBanners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const visible = useHideOnScroll({ threshold: 30 });
  const isVisible = hideOnScroll ? visible : true;

  useEffect(() => {
    let cancelled = false;

    if (!showBanner) {
      setBanners([]);
      setLoaded(true);
      return () => { cancelled = true; };
    }

    setLoaded(false);

    const fetchBanners = () => {
      let url;
      if (isLoginPage) {
        const domain = encodeURIComponent(window.location.hostname);
        url = "/Banner/GetActiveB2BLogin?domain=" + domain;
      } else {
        url = "/Banner/GetActiveB2B";
        if (tenantId) {
          url += "?tenantId=" + tenantId;
        }
      }

      RequestHandler.get(url)
        .then((response) => {
          if (cancelled) return;
          const list = Array.isArray(response) ? response : [];
          setBanners(list.filter((b) => b && b.imagePath));
          setLoaded(true);
        })
        .catch(() => {
          if (cancelled) return;
          setBanners([]);
          setLoaded(true);
        });
    };

    if (controlParameter && !isLoginPage) {
      RequestHandler.get("/FirmAuth/GetActiveTenantInfo").then((response) => {
        if (cancelled) return;
        if (!response?.data?.isShowBannerOnPanel) {
          setBanners([]);
          setLoaded(true);
          return;
        }
        fetchBanners();
      }).catch(() => {
        if (cancelled) return;
        setBanners([]);
        setLoaded(true);
      });
    } else {
      fetchBanners();
    }

    return () => { cancelled = true; };
  }, [showBanner, tenantId, isLoginPage, controlParameter]);

  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClose) {
      onClose();
    }
  };

  const handleBannerClick = (banner) => {
    trackBannerClick({
      bannerId: banner.id,
      screenName,
      panelType,
      firmId,
    });
    const target = resolveBannerTargetUrl(banner, { isLogin });
    openBannerTarget(target);
  };

  if (!loaded || banners.length === 0) {
    return null;
  }

  const settings = {
    dots: banners.length > 1,
    infinite: banners.length > 1,
    autoplay: banners.length > 1,
    autoplaySpeed: 10000,
    speed: 600,
    fade: true,
    arrows: false,
    pauseOnHover: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
  };

  const containerClass = [
    "finekra-banner-slider",
    isVisible ? "" : "finekra-banner-slider--hidden",
    screenName === "PosCollection"
      ? "finekra-banner-slider--with-bottom-gap"
      : "",
    closable ? "finekra-banner-slider--closable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass}>
      {closable ? (
        <button
          type="button"
          className="finekra-banner-slider-close"
          onClick={handleCloseClick}
          aria-label="Kapat"
        >
          <CloseOutlined />
        </button>
      ) : null}
      <Slider {...settings}>
        {banners.map((banner) => (
          <div key={banner.id} className="finekra-banner-slide">
            <button
              type="button"
              className="finekra-banner-button"
              onClick={() => handleBannerClick(banner)}
              aria-label={banner.name || "banner"}
            >
              <img src={FinekraOzelBadge} alt="Finekra'ya Özel" className="finekra-banner-badge" />
              <img
                className="finekra-banner-image"
                src={banner.imagePath}
                alt={banner.name || "banner"}
              />
            </button>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default BannerSlider;
